// Dashboard pages shared with people outside InRange, and who each is shared with.
// Mirrors src/inrange_agents/page_sharing.py and lambda_handlers/api/sharing/
// (docs/Y2026-Q4/design-amazon-overview-sharing.md).
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";

export interface SharedPageInfo {
  key: string;
  label: string;
  /** The paths the person may open; the first is where they land. */
  paths: string[];
}

export interface SharesMe {
  external: boolean;
  email?: string;
  /** Staff viewing the page as this person (View as). */
  view_as?: boolean;
  pages: SharedPageInfo[];
}

export interface PageShare {
  email: string;
  /** Null for someone shared before names were kept. */
  first_name: string | null;
  last_name: string | null;
  added_by: string;
  added_at: string;
  last_viewed_at: string | null;
}

/** The pages shared with the signed-in person outside InRange. */
export const useSharedPages = () => {
  const http = useAgentsHttp();
  return useQuery<SharesMe>([QK.sharesMe], () => http<SharesMe>("/shares/me", {}), {
    staleTime: Infinity,
  });
};

export const useShareList = (pageKey: string, enabled: boolean) => {
  const http = useAgentsHttp();
  return useQuery<PageShare[]>(
    [QK.shareList, pageKey],
    async () => (await http<{ shares: PageShare[] }>("/shares/list", { page_key: pageKey })).shares,
    { enabled },
  );
};

export interface ShareInput {
  email: string;
  first_name: string;
  last_name: string;
}

export const useAddShare = (pageKey: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ share: PageShare }, Error, ShareInput>(
    (person) => http<{ share: PageShare }>("/shares/add", { page_key: pageKey, ...person }),
    // The panel shows the error beside the field.
    {
      meta: { silentError: true },
      // The overview's Assign menus and names come from the owners, which read the list.
      onSettled: () => {
        void qc.invalidateQueries([QK.shareList, pageKey]);
        void qc.invalidateQueries([QK.amazonOwners]);
      },
    },
  );
};

export const useNameShare = (pageKey: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ share: PageShare }, Error, ShareInput>(
    (person) => http<{ share: PageShare }>("/shares/name", { page_key: pageKey, ...person }),
    {
      meta: { silentError: true },
      // The overview's Assign menus and names come from the owners, which read the list.
      onSettled: () => {
        void qc.invalidateQueries([QK.shareList, pageKey]);
        void qc.invalidateQueries([QK.amazonOwners]);
      },
    },
  );
};

export const useRemoveShare = (pageKey: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ removed: boolean }, Error, string>(
    (email) => http<{ removed: boolean }>("/shares/remove", { page_key: pageKey, email }),
    {
      meta: { silentError: true },
      // The overview's Assign menus and names come from the owners, which read the list.
      onSettled: () => {
        void qc.invalidateQueries([QK.shareList, pageKey]);
        void qc.invalidateQueries([QK.amazonOwners]);
      },
    },
  );
};

/** Report the view of a shared page now on screen, on each change of view, as BMA's
 * frontends report page visits. Fire and forget: a lost report must not trouble the reader. */
export const useReportView = (pageKey: string, view: string | null) => {
  const http = useAgentsHttp();
  useEffect(() => {
    if (view === null) return;
    void http("/shares/viewed", { page_key: pageKey, view }).catch(() => undefined);
  }, [http, pageKey, view]);
};
